import { useEffect, useState } from 'react';

export type AnchorStatus = 'pending' | 'ready' | 'timeout';

/** Long enough for a slow fetch, short enough not to feel broken. */
const DEFAULT_TIMEOUT_MS = 4000;

/**
 * How many consecutive frames the anchor must hold the same position before we
 * call it settled. Three is enough to ride out a single reflow without adding a
 * delay anyone would notice.
 */
const STABLE_FRAMES = 3;

/**
 * Wait for a tour step's anchor to exist AND to stop moving.
 *
 * Existing is not enough. antd measures the target once when the step opens and
 * does not re-measure on layout shift, so an anchor that moves afterwards leaves
 * the highlight sitting over the wrong part of the page. That is exactly what
 * happened to the Fail to Close step: the "Last Booked Sites" table renders
 * ABOVE it and arrives from its own request, pushing the buttons down after the
 * popover had been placed.
 *
 * So the anchor's rect is sampled each frame and only reported ready once it has
 * held still. The timeout is the backstop: on expiry the step is shown anyway if
 * the element is there, or reported missing so the caller can apply the step's
 * missing-anchor policy. Either way the tour can never sit waiting forever.
 *
 * @param selector  CSS selector to wait for, or null to stay idle
 * @param enabled   false parks the hook without observing
 * @param timeoutMs how long to wait before giving up
 */
export const useAnchorReady = (
    selector: string | null,
    enabled: boolean,
    timeoutMs: number = DEFAULT_TIMEOUT_MS,
): AnchorStatus => {
    const [status, setStatus] = useState<AnchorStatus>('pending');

    useEffect(() => {
        if (!enabled || !selector) {
            setStatus('pending');
            return undefined;
        }

        setStatus('pending');

        let frame = 0;
        let settled = false;
        let stableFrames = 0;
        let lastRect = '';

        const finish = (next: AnchorStatus) => {
            if (settled) return;
            settled = true;
            setStatus(next);
        };

        const tick = () => {
            if (settled) return;

            const element = document.querySelector(selector);
            if (!element) {
                // Not rendered yet. Reset the run so a late arrival still has to
                // hold still before we point at it.
                stableFrames = 0;
                lastRect = '';
                frame = window.requestAnimationFrame(tick);
                return;
            }

            const {
                top, left, width, height,
            } = element.getBoundingClientRect();
            // The page height is part of the check, not just the anchor's own
            // rect. An anchor can sit perfectly still while content that will
            // displace it is still on its way: on the Sites page the Last Booked
            // table loads from a separate request and renders ABOVE the Fail to
            // Close buttons, so the buttons only jump at the moment it lands.
            // Watching the document height means we wait for that too.
            const rect = `${top},${left},${width},${height},${document.body.scrollHeight}`;

            stableFrames = rect === lastRect ? stableFrames + 1 : 0;
            lastRect = rect;

            if (stableFrames >= STABLE_FRAMES) {
                finish('ready');
                return;
            }

            frame = window.requestAnimationFrame(tick);
        };

        frame = window.requestAnimationFrame(tick);

        const timer = window.setTimeout(() => {
            // Out of budget. Show it anyway if it is on the page, even though it
            // may still be shifting: a slightly-off highlight beats no step.
            finish(document.querySelector(selector) ? 'ready' : 'timeout');
        }, timeoutMs);

        return () => {
            settled = true;
            window.cancelAnimationFrame(frame);
            window.clearTimeout(timer);
        };
    }, [selector, enabled, timeoutMs]);

    return status;
};

export default useAnchorReady;
